/**
 * Enterprise Feedback Layer
 * Centralizes ALL user-facing feedback with required defaultValue
 * 
 * TIER STRATEGY:
 * - Tier 1 (notify): Toast notifications - critical, ephemeral, defaultValue REQUIRED
 * - Tier 2 (inline): Error/message strings for setError() patterns
 * - Tier 3 (validation): Pre-built form validation patterns with interpolation
 * - Tier 4 (confirm): Promise-based confirmation dialogs
 * - Tier 5 (prompt): Promise-based input dialogs
 * 
 * Usage:
 *   const { notify, inline, validation, confirm, prompt } = useFeedback();
 *   notify.success('saved', 'Changes saved successfully');
 *   inline.error('access_denied', 'Access denied');
 *   validation.required('Email');
 *   const shouldDelete = await confirm.delete('this item');
 *   const url = await prompt.url();
 * 
 * NOTE: The `defaultValue` parameter aligns with i18next and L* component pattern.
 */

import { toast } from 'sonner';
import { useLocalizations } from '@/hooks/useLocalizations';
import { useFeedbackDialogContext } from '@/contexts/FeedbackDialogContext';

interface FeedbackOptions {
  description?: string;
  duration?: number;
}

interface ConfirmCustomOptions {
  title?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
}

interface PromptCustomOptions {
  title?: string;
  placeholder?: string;
  defaultValue?: string;
  inputType?: 'text' | 'url' | 'email' | 'password' | 'number';
  confirmText?: string;
  cancelText?: string;
  validate?: (value: string) => true | string;
}

/**
 * Enterprise Feedback Hook
 * Provides type-safe, localized feedback with required fallback for toasts
 * 
 * @example
 * // Tier 1: Toast notifications
 * notify.success('saved', 'Changes saved successfully');
 * notify.error('failed', 'Operation failed');
 * 
 * // Tier 2: Inline messages
 * inline.error('access_denied', 'Access denied');
 * 
 * // Tier 3: Validation
 * validation.required('Email');
 */
export const useFeedback = () => {
  const { lFeedback } = useLocalizations();
  const feedbackContext = useFeedbackDialogContext();
  
  // ====== TIER 1: TOAST NOTIFICATIONS ======
  // defaultValue is REQUIRED (TypeScript enforced) - matches i18next pattern
  const notify = {
    success: (key: string, defaultValue: string, options?: FeedbackOptions) => {
      toast.success(lFeedback(`toast.${key}`, { defaultValue }), options);
    },
    error: (key: string, defaultValue: string, options?: FeedbackOptions) => {
      toast.error(lFeedback(`toast.${key}`, { defaultValue }), options);
    },
    info: (key: string, defaultValue: string, options?: FeedbackOptions) => {
      toast.info(lFeedback(`toast.${key}`, { defaultValue }), options);
    },
    warning: (key: string, defaultValue: string, options?: FeedbackOptions) => {
      toast.warning(lFeedback(`toast.${key}`, { defaultValue }), options);
    },
    loading: (key: string, defaultValue: string) => {
      return toast.loading(lFeedback(`toast.${key}`, { defaultValue }));
    },
    dismiss: (toastId?: string | number) => toast.dismiss(toastId),
  };
  
  // ====== TIER 2: INLINE ERROR MESSAGES ======
  // For setError() patterns - returns localized string
  const inline = {
    error: (key: string, defaultValue: string) => 
      lFeedback(`errors.${key}`, { defaultValue }),
    message: (key: string, defaultValue: string) => 
      lFeedback(`messages.${key}`, { defaultValue }),
  };
  
  // ====== TIER 3: FORM VALIDATION MESSAGES ======
  // Pre-built patterns with interpolation
  const validation = {
    required: (field: string) => 
      lFeedback('validation.required', { defaultValue: `${field} is required`, field }),
    email: () => 
      lFeedback('validation.email_invalid', { defaultValue: 'Please enter a valid email' }),
    minLength: (field: string, min: number) => 
      lFeedback('validation.min_length', { defaultValue: `${field} must be at least ${min} characters`, field, min }),
    maxLength: (field: string, max: number) => 
      lFeedback('validation.max_length', { defaultValue: `${field} must be at most ${max} characters`, field, max }),
    pattern: (field: string) =>
      lFeedback('validation.pattern_invalid', { defaultValue: `${field} format is invalid`, field }),
    match: (field: string, targetField: string) =>
      lFeedback('validation.must_match', { defaultValue: `${field} must match ${targetField}`, field, targetField }),
    custom: (key: string, defaultValue: string, values?: Record<string, unknown>) =>
      lFeedback(`validation.${key}`, { defaultValue, ...values }),
  };

  // ====== TIER 4: CONFIRMATION DIALOGS ======
  // Promise-based, returns boolean - replaces window.confirm()
  const confirm = {
    /**
     * Delete confirmation dialog
     * @param entityName - Name of the entity being deleted (e.g., 'this FAQ', 'user')
     * @returns Promise<boolean> - true if confirmed, false if cancelled
     */
    delete: (entityName: string): Promise<boolean> =>
      feedbackContext.showConfirm({
        title: lFeedback('confirm.delete_title', { defaultValue: 'Confirm Delete' }),
        message: lFeedback('confirm.delete_message', {
          defaultValue: `Are you sure you want to delete ${entityName}? This action cannot be undone.`,
          entity: entityName,
        }),
        confirmText: lFeedback('confirm.delete_confirm', { defaultValue: 'Delete' }),
        cancelText: lFeedback('confirm.cancel', { defaultValue: 'Cancel' }),
        variant: 'destructive',
      }),

    /**
     * Discard publication confirmation dialog (soft-delete, recoverable for 30 days)
     * @param entityName - Name of the entity being discarded
     * @returns Promise<boolean> - true if confirmed, false if cancelled
     */
    discardPublication: (entityName: string): Promise<boolean> =>
      feedbackContext.showConfirm({
        title: lFeedback('confirm.discard_publication_title', { defaultValue: 'Move to Discarded?' }),
        message: lFeedback('confirm.discard_publication_message', {
          defaultValue: `${entityName} will be moved to Discarded and permanently removed after 30 days.`,
          entity: entityName,
        }),
        confirmText: lFeedback('confirm.discard_publication_confirm', { defaultValue: 'Discard' }),
        cancelText: lFeedback('confirm.cancel', { defaultValue: 'Cancel' }),
        variant: 'destructive',
      }),

    /**
     * Discard changes confirmation dialog
     * @returns Promise<boolean> - true if confirmed, false if cancelled
     */
    discard: (): Promise<boolean> =>
      feedbackContext.showConfirm({
        title: lFeedback('confirm.discard_title', { defaultValue: 'Discard Changes?' }),
        message: lFeedback('confirm.discard_message', {
          defaultValue: 'You have unsaved changes. Are you sure you want to discard them?',
        }),
        confirmText: lFeedback('confirm.discard_confirm', { defaultValue: 'Discard' }),
        cancelText: lFeedback('confirm.cancel', { defaultValue: 'Cancel' }),
        variant: 'destructive',
      }),

    /**
     * Save or Discard dialog for unsaved changes
     * @returns Promise<'save' | 'discard' | null> - 'save' to save, 'discard' to abandon, null if cancelled
     */
    saveOrDiscard: (): Promise<'save' | 'discard' | null> =>
      feedbackContext.showSaveOrDiscard({
        title: lFeedback('confirm.unsaved_changes_title', { defaultValue: 'Unsaved Changes' }),
        message: lFeedback('confirm.unsaved_changes_message', {
          defaultValue: 'You have unsaved changes. What would you like to do?',
        }),
        saveText: lFeedback('confirm.save', { defaultValue: 'Save' }),
        discardText: lFeedback('confirm.discard', { defaultValue: 'Discard' }),
      }),

    /**
     * Custom confirmation dialog
     * @param key - Localization key prefix (e.g., 'logout' -> 'confirm.logout_title', 'confirm.logout_message')
     * @param defaultMessage - Default message if localization not found
     * @param options - Optional title, confirmText, cancelText, variant
     * @returns Promise<boolean> - true if confirmed, false if cancelled
     */
    custom: (key: string, defaultMessage: string, options?: ConfirmCustomOptions): Promise<boolean> =>
      feedbackContext.showConfirm({
        title: lFeedback(`confirm.${key}_title`, { defaultValue: options?.title || 'Confirm' }),
        message: lFeedback(`confirm.${key}_message`, { defaultValue: defaultMessage }),
        confirmText: lFeedback(`confirm.${key}_confirm`, { defaultValue: options?.confirmText || 'Confirm' }),
        cancelText: lFeedback(`confirm.${key}_cancel`, { defaultValue: options?.cancelText || 'Cancel' }),
        variant: options?.variant || 'default',
      }),
  };

  // ====== TIER 5: INPUT DIALOGS ======
  // Promise-based, returns string | null - replaces window.prompt()
  const prompt = {
    /**
     * URL input dialog with validation
     * @param message - Optional custom message
     * @returns Promise<string | null> - URL if submitted, null if cancelled
     */
    url: (message?: string): Promise<string | null> =>
      feedbackContext.showPrompt({
        title: lFeedback('prompt.url_title', { defaultValue: 'Enter URL' }),
        message: message || lFeedback('prompt.url_message', { defaultValue: 'Please enter a valid URL' }),
        placeholder: 'https://',
        inputType: 'url',
        confirmText: lFeedback('prompt.confirm', { defaultValue: 'OK' }),
        cancelText: lFeedback('prompt.cancel', { defaultValue: 'Cancel' }),
        validate: (value) => {
          try {
            new URL(value);
            return true;
          } catch {
            return lFeedback('prompt.url_invalid', { defaultValue: 'Please enter a valid URL' });
          }
        },
      }),

    /**
     * Text input dialog (key-based for localization)
     * @param key - Localization key suffix (will become prompt.{key}_message)
     * @param defaultMessage - Default message if localization not found
     * @param options - Optional default value and placeholder
     * @returns Promise<string | null> - Text if submitted, null if cancelled
     */
    text: (key: string, defaultMessage: string, options?: { defaultValue?: string; placeholder?: string }): Promise<string | null> =>
      feedbackContext.showPrompt({
        title: lFeedback(`prompt.${key}_title`, { defaultValue: 'Enter Value' }),
        message: lFeedback(`prompt.${key}_message`, { defaultValue: defaultMessage }),
        defaultValue: options?.defaultValue,
        placeholder: options?.placeholder,
        inputType: 'text',
        confirmText: lFeedback(`prompt.${key}_confirm`, { defaultValue: 'OK' }),
        cancelText: lFeedback(`prompt.${key}_cancel`, { defaultValue: 'Cancel' }),
      }),

    /**
     * Email input dialog with validation
     * @param message - Optional custom message
     * @returns Promise<string | null> - Email if submitted, null if cancelled
     */
    email: (message?: string): Promise<string | null> =>
      feedbackContext.showPrompt({
        title: lFeedback('prompt.email_title', { defaultValue: 'Enter Email' }),
        message: message || lFeedback('prompt.email_message', { defaultValue: 'Please enter an email address' }),
        placeholder: 'email@example.com',
        inputType: 'email',
        confirmText: lFeedback('prompt.confirm', { defaultValue: 'OK' }),
        cancelText: lFeedback('prompt.cancel', { defaultValue: 'Cancel' }),
        validate: (value) => {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailRegex.test(value) || lFeedback('prompt.email_invalid', { defaultValue: 'Please enter a valid email address' });
        },
      }),

    /**
     * Number input dialog (key-based for localization)
     * @param key - Localization key suffix (will become prompt.{key}_message)
     * @param defaultMessage - Default message if localization not found
     * @param options - Optional default value, placeholder, min/max validation
     * @returns Promise<string | null> - Number string if submitted, null if cancelled
     */
    number: (key: string, defaultMessage: string, options?: { defaultValue?: string; placeholder?: string; min?: number; max?: number }): Promise<string | null> =>
      feedbackContext.showPrompt({
        title: lFeedback(`prompt.${key}_title`, { defaultValue: 'Enter Number' }),
        message: lFeedback(`prompt.${key}_message`, { defaultValue: defaultMessage }),
        defaultValue: options?.defaultValue,
        placeholder: options?.placeholder,
        inputType: 'number',
        confirmText: lFeedback(`prompt.${key}_confirm`, { defaultValue: 'OK' }),
        cancelText: lFeedback(`prompt.${key}_cancel`, { defaultValue: 'Cancel' }),
        validate: (value) => {
          const num = Number(value);
          if (isNaN(num)) {
            return lFeedback('prompt.number_invalid', { defaultValue: 'Please enter a valid number' });
          }
          if (options?.min !== undefined && num < options.min) {
            return lFeedback('prompt.number_min', { defaultValue: `Value must be at least ${options.min}`, min: options.min });
          }
          if (options?.max !== undefined && num > options.max) {
            return lFeedback('prompt.number_max', { defaultValue: `Value must be at most ${options.max}`, max: options.max });
          }
          return true;
        },
      }),

    /**
     * Custom input dialog
     * @param key - Localization key prefix
     * @param message - Default message
     * @param options - Full customization options
     * @returns Promise<string | null> - Value if submitted, null if cancelled
     */
    custom: (key: string, message: string, options?: PromptCustomOptions): Promise<string | null> =>
      feedbackContext.showPrompt({
        title: lFeedback(`prompt.${key}_title`, { defaultValue: options?.title || 'Enter Value' }),
        message: lFeedback(`prompt.${key}_message`, { defaultValue: message }),
        defaultValue: options?.defaultValue,
        placeholder: options?.placeholder,
        inputType: options?.inputType || 'text',
        confirmText: lFeedback(`prompt.${key}_confirm`, { defaultValue: options?.confirmText || 'OK' }),
        cancelText: lFeedback(`prompt.${key}_cancel`, { defaultValue: options?.cancelText || 'Cancel' }),
        validate: options?.validate,
      }),
  };

  return { notify, inline, validation, confirm, prompt };
};
